/**
 * The `shell` i18n namespace — strings that belong to the SPA chrome itself.
 *
 * OWNERSHIP BOUNDARY (ARCHITECTURE §10):
 * - Domain copy lives in `packages/shared/src/i18n/{sv,en,zh}/<module>.json` and
 *   is owned by that module's agent.
 * - Chrome copy (shell layout, data-table controls, auth screens, settings,
 *   placeholders) lives HERE, inside apps/web, because it belongs to the web
 *   shell and no module agent should ever have to edit a shared catalog to ship
 *   a page. `common` in @warehouse/shared stays the cross-client catalog (nav
 *   labels, error codes, action verbs) shared with Android and the mailer.
 *
 * `sv` is the source of truth; `en`/`zh` are typed `satisfies ShellCatalog`, so
 * adding a Swedish key is a compile error until the other two follow — the same
 * guard the shared catalogs use.
 */

const sv = {
  underConstruction: {
    title: 'Under uppbyggnad',
    description: 'Den här vyn byggs just nu. Den aktiveras när modulen är klar.',
  },
  layout: {
    skipToContent: 'Hoppa till innehåll',
    openMenu: 'Öppna menyn',
    closeMenu: 'Stäng menyn',
    mainNavigation: 'Huvudmeny',
    userMenu: 'Kontomeny',
    adminApp: 'Lager',
    portalApp: 'Portal',
    goToPortal: 'Till beställningsportalen',
    goToAdmin: 'Till lagersystemet',
    connected: 'Realtid ansluten',
    reconnecting: 'Återansluter…',
    disconnected: 'Realtid frånkopplad',
  },
  table: {
    searchPlaceholder: 'Sök…',
    filters: 'Filter',
    clearFilters: 'Rensa filter',
    clearSearch: 'Rensa sökningen',
    empty: 'Inga träffar',
    emptyDescription: 'Prova att ändra sökningen eller rensa filtren.',
    loadFailed: 'Listan kunde inte hämtas',
    sortAscending: 'Sortera stigande',
    sortDescending: 'Sortera fallande',
    sortNone: 'Ta bort sortering',
    selected: '{{count}} markerade',
    rowsPerPage: 'Rader per sida',
    of: 'av',
    loadMore: 'Visa fler',
  },
  auth: {
    signInTitle: 'Gigachop WMS',
    signInSubtitle: 'Logga in med ditt Gigachop-konto för att fortsätta.',
    signInWithEms: 'Logga in med Gigachop EMS',
    signingIn: 'Loggar in…',
    completing: 'Slutför inloggningen…',
    failedTitle: 'Inloggningen misslyckades',
    tryAgain: 'Försök igen',
    devLogin: 'Utvecklarinloggning',
    devLoginTitle: 'Utvecklarinloggning',
    devLoginDescription:
      'Loggar in som en seedad testanvändare utan att gå via EMS. Endast tillgänglig när DEV_AUTH_BYPASS är aktiverat på servern.',
    devLoginUnavailable: 'Utvecklarinloggning är inte aktiverad på den här servern.',
    devLoginEmail: 'E-post',
    ssoNotConfigured: 'EMS-inloggning är inte konfigurerad i den här bygget.',
    signOut: 'Logga ut',
    signedOutTitle: 'Du är utloggad',
    noAccessTitle: 'Ingen behörighet',
    noAccessDescription: 'Ditt konto saknar behörighet till den här vyn. Kontakta en administratör.',
    backToStart: 'Till startsidan',
  },
  settings: {
    title: 'Inställningar',
    subtitle: 'Ditt konto och dina inställningar i lagersystemet.',
    profileSection: 'Profil',
    name: 'Namn',
    email: 'E-post',
    roles: 'Roller',
    permissionsCount: '{{count}} behörigheter',
    languageSection: 'Språk',
    languageHelp: 'Språket sparas på ditt konto och används även i e-post från systemet.',
    languageSaved: 'Språket har uppdaterats',
    sessionSection: 'Session',
    sessionKind: 'Typ',
    sessionKindWeb: 'Webbläsare',
    sessionKindDevice: 'Handdator',
    sessionExpires: 'Giltig till',
    signOutEverywhere: 'Logga ut',
    signOutConfirmTitle: 'Logga ut?',
    signOutConfirmMessage: 'Du loggas ut från lagersystemet i den här webbläsaren.',
    permissionsSection: 'Behörigheter',
    permissionsHelp:
      'Behörigheter styrs av dina roller i EMS. Menyval du saknar behörighet till visas inte — servern kontrollerar alltid behörigheten igen.',
    noPermissions: 'Inga behörigheter',
  },
  dashboard: {
    title: 'Översikt',
    welcome: 'Hej {{name}}!',
    subtitle: 'Snabbvy över lagret. Nyckeltal fylls på när modulerna aktiveras.',
    quickLinks: 'Genvägar',
  },
  portal: {
    title: 'Beställningsportal',
    order: 'Beställ',
    myOrders: 'Mina ordrar',
    receive: 'Godsmottagning',
    claims: 'Reklamationer',
  },
  exceptions: {
    title: 'Avvikelser',
    subtitle: 'Avvisade kommandon från handdatorer och andra avvikelser som kräver åtgärd.',
  },
  errors: {
    /** Client-only: the request never reached the api. */
    E_NETWORK: 'Ingen kontakt med servern — kontrollera uppkopplingen',
    pageTitle: 'Något gick fel',
    pageDescription: 'Vyn kunde inte visas. Ladda om sidan eller gå tillbaka.',
    reload: 'Ladda om',
    notFoundTitle: 'Sidan finns inte',
    notFoundDescription: 'Adressen leder ingenstans i lagersystemet.',
  },
};

/**
 * Values are plain `string` (no `as const`): the type is a SHAPE contract, not
 * a set of Swedish literals — otherwise `en` would have to equal `sv`.
 */
export type ShellCatalog = typeof sv;

const en = {
  underConstruction: {
    title: 'Under construction',
    description: 'This view is being built. It goes live when the module ships.',
  },
  layout: {
    skipToContent: 'Skip to content',
    openMenu: 'Open menu',
    closeMenu: 'Close menu',
    mainNavigation: 'Main navigation',
    userMenu: 'Account menu',
    adminApp: 'Warehouse',
    portalApp: 'Portal',
    goToPortal: 'Go to the ordering portal',
    goToAdmin: 'Go to the warehouse app',
    connected: 'Live updates connected',
    reconnecting: 'Reconnecting…',
    disconnected: 'Live updates disconnected',
  },
  table: {
    searchPlaceholder: 'Search…',
    filters: 'Filters',
    clearFilters: 'Clear filters',
    clearSearch: 'Clear the search',
    empty: 'No matches',
    emptyDescription: 'Try a different search or clear the filters.',
    loadFailed: 'The list could not be loaded',
    sortAscending: 'Sort ascending',
    sortDescending: 'Sort descending',
    sortNone: 'Remove sorting',
    selected: '{{count}} selected',
    rowsPerPage: 'Rows per page',
    of: 'of',
    loadMore: 'Show more',
  },
  auth: {
    signInTitle: 'Gigachop WMS',
    signInSubtitle: 'Sign in with your Gigachop account to continue.',
    signInWithEms: 'Sign in with Gigachop EMS',
    signingIn: 'Signing in…',
    completing: 'Completing sign-in…',
    failedTitle: 'Sign-in failed',
    tryAgain: 'Try again',
    devLogin: 'Developer sign-in',
    devLoginTitle: 'Developer sign-in',
    devLoginDescription:
      'Signs in as a seeded test user without going through EMS. Only available while DEV_AUTH_BYPASS is enabled on the server.',
    devLoginUnavailable: 'Developer sign-in is not enabled on this server.',
    devLoginEmail: 'Email',
    ssoNotConfigured: 'EMS sign-in is not configured in this build.',
    signOut: 'Sign out',
    signedOutTitle: 'You are signed out',
    noAccessTitle: 'No access',
    noAccessDescription: 'Your account lacks permission for this view. Contact an administrator.',
    backToStart: 'Back to start',
  },
  settings: {
    title: 'Settings',
    subtitle: 'Your account and preferences in the warehouse system.',
    profileSection: 'Profile',
    name: 'Name',
    email: 'Email',
    roles: 'Roles',
    permissionsCount: '{{count}} permissions',
    languageSection: 'Language',
    languageHelp: 'The language is stored on your account and is also used for email from the system.',
    languageSaved: 'Language updated',
    sessionSection: 'Session',
    sessionKind: 'Type',
    sessionKindWeb: 'Browser',
    sessionKindDevice: 'Handheld',
    sessionExpires: 'Valid until',
    signOutEverywhere: 'Sign out',
    signOutConfirmTitle: 'Sign out?',
    signOutConfirmMessage: 'You will be signed out of the warehouse system in this browser.',
    permissionsSection: 'Permissions',
    permissionsHelp:
      'Permissions come from your EMS roles. Menu entries you lack permission for are hidden — the server always re-checks.',
    noPermissions: 'No permissions',
  },
  dashboard: {
    title: 'Overview',
    welcome: 'Hi {{name}}!',
    subtitle: 'A quick view of the warehouse. Metrics fill in as modules go live.',
    quickLinks: 'Shortcuts',
  },
  portal: {
    title: 'Ordering portal',
    order: 'Order',
    myOrders: 'My orders',
    receive: 'Receive goods',
    claims: 'Claims',
  },
  exceptions: {
    title: 'Exceptions',
    subtitle: 'Rejected scanner commands and other exceptions that need attention.',
  },
  errors: {
    E_NETWORK: 'No contact with the server — check your connection',
    pageTitle: 'Something went wrong',
    pageDescription: 'This view could not be rendered. Reload the page or go back.',
    reload: 'Reload',
    notFoundTitle: 'Page not found',
    notFoundDescription: 'That address does not exist in the warehouse system.',
  },
} satisfies ShellCatalog;

const zh = {
  underConstruction: {
    title: '建设中',
    description: '此页面正在开发中，模块完成后即可使用。',
  },
  layout: {
    skipToContent: '跳到主要内容',
    openMenu: '打开菜单',
    closeMenu: '关闭菜单',
    mainNavigation: '主导航',
    userMenu: '账户菜单',
    adminApp: '仓库',
    portalApp: '门户',
    goToPortal: '前往订购门户',
    goToAdmin: '前往仓库系统',
    connected: '实时连接已建立',
    reconnecting: '正在重新连接…',
    disconnected: '实时连接已断开',
  },
  table: {
    searchPlaceholder: '搜索…',
    filters: '筛选',
    clearFilters: '清除筛选',
    clearSearch: '清除搜索',
    empty: '没有结果',
    emptyDescription: '请更改搜索条件或清除筛选。',
    loadFailed: '无法加载列表',
    sortAscending: '升序排序',
    sortDescending: '降序排序',
    sortNone: '取消排序',
    selected: '已选 {{count}} 项',
    rowsPerPage: '每页行数',
    of: '/',
    loadMore: '显示更多',
  },
  auth: {
    signInTitle: 'Gigachop WMS',
    signInSubtitle: '请使用您的 Gigachop 账户登录以继续。',
    signInWithEms: '使用 Gigachop EMS 登录',
    signingIn: '正在登录…',
    completing: '正在完成登录…',
    failedTitle: '登录失败',
    tryAgain: '重试',
    devLogin: '开发者登录',
    devLoginTitle: '开发者登录',
    devLoginDescription: '以预置测试用户身份登录，无需经过 EMS。仅在服务器启用 DEV_AUTH_BYPASS 时可用。',
    devLoginUnavailable: '此服务器未启用开发者登录。',
    devLoginEmail: '电子邮件',
    ssoNotConfigured: '此版本未配置 EMS 登录。',
    signOut: '退出登录',
    signedOutTitle: '您已退出登录',
    noAccessTitle: '没有权限',
    noAccessDescription: '您的账户没有查看此页面的权限，请联系管理员。',
    backToStart: '返回首页',
  },
  settings: {
    title: '设置',
    subtitle: '您在仓库系统中的账户与偏好设置。',
    profileSection: '个人资料',
    name: '姓名',
    email: '电子邮件',
    roles: '角色',
    permissionsCount: '{{count}} 项权限',
    languageSection: '语言',
    languageHelp: '语言会保存在您的账户中，系统邮件也会使用该语言。',
    languageSaved: '语言已更新',
    sessionSection: '会话',
    sessionKind: '类型',
    sessionKindWeb: '浏览器',
    sessionKindDevice: '手持终端',
    sessionExpires: '有效期至',
    signOutEverywhere: '退出登录',
    signOutConfirmTitle: '退出登录？',
    signOutConfirmMessage: '您将在此浏览器中退出仓库系统。',
    permissionsSection: '权限',
    permissionsHelp: '权限来自您在 EMS 中的角色。没有权限的菜单项会被隐藏，服务器始终会再次校验。',
    noPermissions: '没有权限',
  },
  dashboard: {
    title: '概览',
    welcome: '你好，{{name}}！',
    subtitle: '仓库概览。各模块上线后将显示关键指标。',
    quickLinks: '快捷入口',
  },
  portal: {
    title: '订购门户',
    order: '下单',
    myOrders: '我的订单',
    receive: '收货',
    claims: '索赔',
  },
  exceptions: {
    title: '异常',
    subtitle: '被拒绝的手持终端指令以及其他需要处理的异常。',
  },
  errors: {
    E_NETWORK: '无法连接服务器 — 请检查网络连接',
    pageTitle: '出错了',
    pageDescription: '无法显示此页面。请刷新页面或返回。',
    reload: '刷新',
    notFoundTitle: '页面不存在',
    notFoundDescription: '仓库系统中不存在该地址。',
  },
} satisfies ShellCatalog;

export const SHELL_NAMESPACE = 'shell';

export const shellResources = { sv, en, zh } as const;
